import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { formatMoney } from '../../components/data-grid/money';
import type { Problem } from '../../components/data-grid/problem';
import { useI18n } from '../../i18n/I18nProvider';
import { StatusBadge } from '../documents/DocumentsView';
import type { DocumentStatus } from '../documents/filters';
import {
  accountLabel,
  availableOperations,
  computeBalance,
  dimensionRulesFor,
  leafAccounts,
  statusKeyOf,
} from './form';
import type {
  AccountOption,
  DocumentFormState,
  DocumentTypeView,
  FieldDefinitionView,
  FormLine,
} from './form';

export interface ChainNodeView {
  documentId: string;
  number: string;
  type: string;
  status: DocumentStatus;
}

export interface ChainGraphView {
  up: ChainNodeView[];
  down: ChainNodeView[];
}

export interface AttachmentView {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  checksum: string;
  createdAt: string;
  uploadedBy?: string | null;
}

export interface DocumentFormViewProps {
  type: DocumentTypeView;
  document: DocumentFormState;
  accounts: readonly AccountOption[];
  permissions: readonly string[];
  chain: ChainGraphView;
  attachments: readonly AttachmentView[];
  dimensionValues?: Record<string, string[]>;
  problem?: Problem | null;
  saving?: boolean;
  onHeaderChange(name: string, value: string): void;
  onLineChange(lineKey: string, field: string, value: string): void;
  onDimensionChange(lineKey: string, slot: number, value: string): void;
  onAddLine(): void;
  onRemoveLine(lineKey: string): void;
  onSave(): void;
  onApprove(): void;
  onPost(): void;
  onCancel(reason: string): void;
  onOpenDocument(documentId: string): void;
  onUpload(file: File): void;
  onDeleteAttachment(attachmentId: string): void;
}

const COLUMN_LABEL_KEY: Record<string, string> = {
  debitAccount: 'documentFormColumnDebit',
  creditAccount: 'documentFormColumnCredit',
  amount: 'documentFormColumnAmount',
  side: 'documentFormColumnSide',
};

export function formatBytes(size: number): string {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: FieldDefinitionView;
  value: unknown;
  onChange(value: string): void;
}): JSX.Element {
  const text = value === undefined || value === null ? '' : String(value);
  if (field.type === 'boolean') {
    return (
      <input
        type="checkbox"
        name={field.name}
        checked={text === 'true'}
        onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
      />
    );
  }
  if (field.type === 'enum') {
    return (
      <select className="input" name={field.name} value={text} onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {(field.options ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === 'text') {
    return (
      <textarea className="input" name={field.name} value={text} onChange={(event) => onChange(event.target.value)} />
    );
  }
  const inputMode = field.type === 'number' || field.type === 'amount' || field.type === 'quantity' ? 'decimal' : undefined;
  return (
    <input
      className="input"
      name={field.name}
      type={field.type === 'date' ? 'date' : 'text'}
      inputMode={inputMode}
      value={text}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function AccountSelect({
  line,
  name,
  accounts,
  onChange,
}: {
  line: FormLine;
  name: string;
  accounts: readonly AccountOption[];
  onChange(value: string): void;
}): JSX.Element {
  const { t } = useI18n();
  const current = typeof line.data[name] === 'string' ? (line.data[name] as string) : '';
  return (
    <select className="input" name={name} value={current} onChange={(event) => onChange(event.target.value)}>
      <option value="">{t('documentFormChooseAccount')}</option>
      {leafAccounts(accounts).map((account) => (
        <option key={account.id} value={account.code}>
          {accountLabel(account)}
        </option>
      ))}
    </select>
  );
}

export function DocumentFormView({
  type,
  document,
  accounts,
  permissions,
  chain,
  attachments,
  dimensionValues = {},
  problem = null,
  saving = false,
  onHeaderChange,
  onLineChange,
  onDimensionChange,
  onAddLine,
  onRemoveLine,
  onSave,
  onApprove,
  onPost,
  onCancel,
  onOpenDocument,
  onUpload,
  onDeleteAttachment,
}: DocumentFormViewProps): JSX.Element {
  const { t, language } = useI18n();
  const isCard = document.id !== undefined;
  const operation = availableOperations(type, document.status, permissions, !isCard);
  const balance = computeBalance(document.lines);
  const canEdit = operation.has('save');
  // Attachments hang off a stored document id; before the first save there is none.
  const canAttach = canEdit && isCard;
  const [cancelReason, setCancelReason] = useState('');

  return (
    <section>
      <div className="page-head" style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1>{isCard ? document.number : t('documentFormTitleNew')}</h1>
          <p className="muted">
            {type.name} · {t('documentFormSubtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {operation.has('cancel') ? (
            <>
              <input
                className="input"
                data-testid="cancel-reason"
                placeholder={t('documentFormCancelReason')}
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
              />
              <button
                type="button"
                className="btn"
                data-testid="document-cancel"
                onClick={() => onCancel(cancelReason)}
              >
                {t('documentFormCancel')}
              </button>
            </>
          ) : null}
          {operation.has('approve') ? (
            <button type="button" className="btn" data-testid="document-approve" onClick={onApprove}>
              {t('documentFormApprove')}
            </button>
          ) : null}
          {operation.has('post') ? (
            <button
              type="button"
              className="btn btn--primary"
              data-testid="document-post"
              disabled={!balance.balanced || saving}
              title={balance.balanced ? undefined : t('documentFormUnbalancedHint')}
              onClick={onPost}
            >
              {t('documentFormPost')}
            </button>
          ) : null}
          {operation.has('save') ? (
            <button type="button" className="btn" data-testid="document-save" disabled={saving} onClick={onSave}>
              {t('documentFormSave')}
            </button>
          ) : null}
        </div>
      </div>

      {type.statusModel.approvalEnabled && (document.status === 'pending' || document.status === 'approved') ? (
        <div
          className="alert"
          data-testid="approval-banner"
          style={{ marginTop: 12, background: 'var(--primary-subtle)', color: 'var(--primary)' }}
        >
          {document.status === 'pending' ? t('documentFormApprovalPending') : t('documentFormApprovalApproved')}
        </div>
      ) : null}

      {problem !== null ? (
        <div className="alert alert--danger" role="alert" data-testid="document-problem" style={{ marginTop: 12 }}>
          <strong>{t('documentFormProblemTitle')}</strong> <code>{problem.code}</code> — {problem.detail}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 16, marginTop: 16 }}>
        <div>
          <div className="card">
            <h3>{t('documentFormSectionHeader')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 12 }}>
              {type.fields.map((field) => (
                <label key={field.name} className="field">
                  <span>
                    {field.name}
                    {field.required === true ? ' *' : ''}
                  </span>
                  <FieldControl
                    field={field}
                    value={document.data[field.name]}
                    onChange={(value) => onHeaderChange(field.name, value)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3>{t('documentFormSectionLines')}</h3>
              <span className="muted">
                {document.lines.length} {t('documentFormColumnLine')}
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }} data-testid="document-lines">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: 32 }}>{t('documentFormColumnLine')}</th>
                  {type.tableFields.fields.map((field) => (
                    <th key={field.name} style={{ textAlign: 'left' }}>
                      {COLUMN_LABEL_KEY[field.name] === undefined ? field.name : t(COLUMN_LABEL_KEY[field.name] as string)}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {document.lines.length === 0 ? (
                  <tr>
                    <td colSpan={type.tableFields.fields.length + 2} className="muted" style={{ padding: 12 }}>
                      {t('documentFormNoLines')}
                    </td>
                  </tr>
                ) : null}
                {document.lines.map((line: FormLine) => {
                  const rules = dimensionRulesFor(line, accounts);
                  return (
                    <tr key={line.key} data-line-key={line.key}>
                      <td>{line.lineNo}</td>
                      {type.tableFields.fields.map((field) => (
                        <td key={field.name} style={{ padding: '4px 6px' }}>
                          {field.name === 'debitAccount' || field.name === 'creditAccount' ? (
                            <AccountSelect
                              line={line}
                              name={field.name}
                              accounts={accounts}
                              onChange={(value) => onLineChange(line.key, field.name, value)}
                            />
                          ) : field.name === 'side' ? (
                            <select
                              className="input"
                              value={typeof line.data.side === 'string' ? line.data.side : ''}
                              onChange={(event) => onLineChange(line.key, 'side', event.target.value)}
                            >
                              <option value="">—</option>
                              <option value="D">{t('documentFormSideDebit')}</option>
                              <option value="C">{t('documentFormSideCredit')}</option>
                            </select>
                          ) : field.name === 'amount' ? (
                            <input
                              className="input"
                              inputMode="decimal"
                              value={line.amount}
                              onChange={(event) => onLineChange(line.key, 'amount', event.target.value)}
                            />
                          ) : (
                            <FieldControl
                              field={field}
                              value={line.data[field.name]}
                              onChange={(value) => onLineChange(line.key, field.name, value)}
                            />
                          )}
                        </td>
                      ))}
                      <td>
                        <button
                          type="button"
                          className="btn"
                          aria-label={t('documentFormRemoveLine')}
                          disabled={!canEdit}
                          onClick={() => onRemoveLine(line.key)}
                        >
                          ×
                        </button>
                      </td>
                      {rules.map((rule) => (
                        <td key={`dim-${rule.slot}`} data-dim-slot={rule.slot}>
                          {dimensionValues[rule.dimType] === undefined ? (
                            <input
                              className="input"
                              aria-label={`${t('documentFormDimensionPrefix')} ${rule.dimType}`}
                              value={line.dimensions[`dim${rule.slot}`] ?? ''}
                              onChange={(event) => onDimensionChange(line.key, rule.slot, event.target.value)}
                            />
                          ) : (
                            <select
                              className="input"
                              aria-label={`${t('documentFormDimensionPrefix')} ${rule.dimType}`}
                              value={line.dimensions[`dim${rule.slot}`] ?? ''}
                              onChange={(event) => onDimensionChange(line.key, rule.slot, event.target.value)}
                            >
                              <option value="">—</option>
                              {(dimensionValues[rule.dimType] ?? []).map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button type="button" className="btn" style={{ marginTop: 8 }} disabled={!canEdit} onClick={onAddLine}>
              {t('documentFormAddLine')}
            </button>

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 24, marginTop: 12 }}
              data-testid="document-totals"
            >
              <span>
                {t('documentFormTotalsDebit')} <strong data-testid="total-debit">{formatMoney(balance.debit, language)}</strong>
              </span>
              <span>
                {t('documentFormTotalsCredit')}{' '}
                <strong data-testid="total-credit">{formatMoney(balance.credit, language)}</strong>
              </span>
              <span>
                {t('documentFormTotalsDifference')}{' '}
                <strong data-testid="total-difference">{formatMoney(balance.difference, language)}</strong>
              </span>
              <span
                className="badge"
                data-testid="balance-badge"
                data-balanced={balance.balanced ? 'true' : 'false'}
                style={{ color: balance.balanced ? '#1f7a4d' : '#a33' }}
              >
                {balance.balanced ? t('documentFormBalanced') : t('documentFormUnbalanced')}
              </span>
            </div>
            {!balance.balanced ? (
              <p className="muted" data-testid="balance-hint" style={{ marginTop: 6, color: '#a33' }}>
                {t('documentFormUnbalancedHint')} {t('documentFormTotalsDifference')}: {formatMoney(balance.difference, language)}
              </p>
            ) : null}
          </div>
        </div>

        <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" data-testid="document-meta">
            <h3>{t('documentFormTitleCard')}</h3>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 6, marginTop: 8 }}>
              <dt className="muted">{t('documentFormNumberLabel')}</dt>
              <dd style={{ margin: 0 }}>{document.number}</dd>
              <dt className="muted">{t('documentFormTypeLabel')}</dt>
              <dd style={{ margin: 0 }}>{type.name}</dd>
              <dt className="muted">{t('documentFormStatusLabel')}</dt>
              <dd style={{ margin: 0 }}>
                <StatusBadge status={document.status} />
                <span className="muted"> {t(statusKeyOf(document.status))}</span>
              </dd>
            </dl>
          </div>

          <div className="card" data-testid="chain-panel">
            <h3>{t('documentFormChainTitle')}</h3>
            {chain.up.length === 0 && chain.down.length === 0 ? (
              <p className="muted">{t('documentFormChainEmpty')}</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {chain.up.map((node) => (
                  <li key={node.documentId} data-chain-node="up" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="badge">{t('documentFormChainBase')}</span>
                    <a
                      href={`/documents/${node.documentId}`}
                      onClick={(event) => {
                        event.preventDefault();
                        onOpenDocument(node.documentId);
                      }}
                    >
                      {node.number}
                    </a>
                    <StatusBadge status={node.status} />
                  </li>
                ))}
                <li data-chain-node="current" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="badge">{t('documentFormStatusLabel')}</span>
                  <span>{document.number}</span>
                  <StatusBadge status={document.status} />
                </li>
                {chain.down.map((node) => (
                  <li key={node.documentId} data-chain-node="down" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="badge">{t('documentFormChainChild')}</span>
                    <a
                      href={`/documents/${node.documentId}`}
                      onClick={(event) => {
                        event.preventDefault();
                        onOpenDocument(node.documentId);
                      }}
                    >
                      {node.number}
                    </a>
                    <StatusBadge status={node.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card" data-testid="attachments-panel">
            <h3>{t('documentFormAttachmentsTitle')}</h3>
            {attachments.length === 0 ? (
              <p className="muted">{t('documentFormAttachmentsEmpty')}</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>{t('documentFormAttachmentName')}</th>
                    <th style={{ textAlign: 'right' }}>{t('documentFormAttachmentSize')}</th>
                    <th style={{ textAlign: 'left' }}>{t('documentFormAttachmentUploader')}</th>
                    <th style={{ textAlign: 'left' }}>{t('documentFormAttachmentChecksum')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {attachments.map((attachment) => (
                    <tr key={attachment.id} data-attachment-id={attachment.id}>
                      <td>{attachment.fileName}</td>
                      <td style={{ textAlign: 'right' }}>{formatBytes(attachment.size)}</td>
                      <td>{attachment.uploadedBy ?? '—'}</td>
                      <td>
                        <code>{attachment.checksum}</code>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn"
                          aria-label={t('documentFormAttachmentDelete')}
                          disabled={!canEdit}
                          onClick={() => onDeleteAttachment(attachment.id)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <label
              data-testid="attachment-upload"
              className="btn"
              style={{ display: 'inline-block', marginTop: 8, cursor: canAttach ? 'pointer' : 'not-allowed' }}
            >
              {t('documentFormAttachmentUpload')}
              <input
                type="file"
                name="attachment"
                style={{ display: 'none' }}
                disabled={!canAttach}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) {
                    onUpload(file);
                  }
                }}
              />
            </label>
          </div>
        </aside>
      </div>
    </section>
  );
}
